import React, { FC, useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import { SliceStop, updateStopV2 } from '../../redux/features/orders/ordersSlice';
import Box from '@mui/material/Box';
import { StopOrderV2 } from '../TwStop/TwStop';
import DateUtils from '../../utils/dateUtils';
import Grid from '@mui/material/Grid';
import TwAutocomplete, { TwAutocompleteOption } from '../TwAutocomplete/TwAutocomplete';
import { STOP_TYPE, STOP_TYPES } from '../../redux/api/trips/trips.model';
import TwField, { TwInputTypes } from '../TwFields/TwFields';
import StopNoteContactInfo from '../TwStop/components/StopNoteContactInfo';
import { Characters, useUnformatValue } from '../../hooks/useAllowCharacters';
import TwDatePicker from '../TwDatePicker/TwDatePicker';
import { useGetCreateLoadConfigQuery } from '../../redux/api/load/loadApi';
import { CreateLoadFields, LoadLocation } from '../../redux/api/load/load.model';
import LoadUtils from '../../utils/loadUtils';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { FormikValues, useFormikContext } from 'formik';
import useAuth from '../../providers/Auth/Auth.provider';
import TwTimePicker from '../TwTimePicker/TwTimePicker';
import { TZ_USAGE } from '../../redux/api/orders/orders.model';
import { parse } from 'date-fns';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';

interface TwSharedStopProps {
  stop: SliceStop;
  hasLocationBeenUpdated: boolean;
  isImport: boolean;
  isCreateOrder?: boolean;
}

const TwOnlyinfoStop: FC<TwSharedStopProps> = ({
  stop,
  hasLocationBeenUpdated,
  isImport,
  isCreateOrder
}) => {
  const {
    getTenantSetting,
    data: { userData }
  } = useAuth();
  const dispatch = useAppDispatch();
  const { status, timezone_usage } = useAppSelector((state) => state.orders);
  const { data: createLoadConfig } = useGetCreateLoadConfigQuery();
  const { errors } = useFormikContext<FormikValues>();
  const unformatString = useUnformatValue(Characters.number);
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const enableAppointmentWindow = getTenantSetting('enable-appointment-window', false);
  const use24HourClock = getTenantSetting('use-24-hour-clock', false);

  const [requiredFields, setRequiredFields] = useState<CreateLoadFields[] | never>([]);
  const [date, setDate] = useState<Date | null | undefined>(null);
  const [timeFrom, setTimeFrom] = useState<Date | null | undefined>(null);
  const [timeTo, setTimeTo] = useState<Date | null | undefined>(null);
  const [stopIdentifier, setStopIdentifier] = useState<string | undefined>(
    stop.provider_stop_identifier
  );

  const handleLocationNotesChange = (value: string, field: string): void => {
    const newStop = { ...stop, [field]: value };
    dispatch(updateStopV2({ stop: newStop }));
  };

  const handleLocationContactChange = (value: string, field: string): void => {
    let newStop = { ...stop };
    if (field === 'contact_number') {
      newStop = { ...stop, contact: { ...stop.contact, contact_number: unformatString(value) } };
    } else {
      newStop = { ...stop, contact: { ...stop.contact, [field]: value } };
    }

    dispatch(updateStopV2({ stop: newStop }));
  };

  useEffect(() => {
    const required = LoadUtils.getRequiredFields(status, createLoadConfig || []);
    setRequiredFields(required);
  }, [status]);

  // #region Effects to update to the redux store
  useEffect(() => {
    const newStop = { ...stop };
    newStop.provider_date = date;
    dispatch(updateStopV2({ stop: newStop }));
  }, [date]);

  useEffect(() => {
    const newStop = { ...stop };
    newStop.provider_time_from = timeFrom;

    dispatch(updateStopV2({ stop: newStop }));
  }, [timeFrom]);

  useEffect(() => {
    const newStop = { ...stop };
    newStop.provider_time_to = timeTo;

    dispatch(updateStopV2({ stop: newStop }));
  }, [timeTo]);

  // #endregion of effects to redux store

  return (
    <Box className="stop__onlyinfo-container" key={stop.ui_id}>
      <Box display={'flex'}>
        <StopOrderV2 islast={'false'}>{stop.order}</StopOrderV2>
        <Typography component="span" sx={{ fontWeight: '600', color: '#1F2937', fontSize: '14px' }}>
          Stop
        </Typography>
      </Box>

      <Grid container gap={1}>
        <Grid item xs={7.5}>
          <TwField
            name={`shared-stop-location-${stop.order}`}
            label="Stop location"
            inputType={TwInputTypes.text}
            value={stop.provider_location?.label}
            disabled
            sx={{
              '.MuiInputBase-root.Mui-disabled, .MuiInputBase-root.Mui-disabled  input.MuiInputBase-input.Mui-disabled, .MuiInputBase-root.Mui-disabled button.MuiButtonBase-root.Mui-disabled':
                {
                  backgroundColor: '#F9FAFB'
                }
            }}
          />
        </Grid>
        <Grid item xs={2}>
          <TwAutocomplete
            name={`shared-stop-type-${stop.order}`}
            label="Type"
            optionList={STOP_TYPES}
            value={stop.provider_stop_type}
            onChange={(_, newValue: TwAutocompleteOption | null): void => {
              if (!newValue) return;
              const newStop = { ...stop };
              newStop.stop_type = newValue.id as STOP_TYPE;
              newStop.provider_stop_type = newValue;
              dispatch(updateStopV2({ stop: newStop }));
            }}
          />
        </Grid>
        <Grid item xs={2}>
          <TwField
            id={`shared-stop-pickupnumber-${stop.order}`}
            name={`shared-stop-pickupnumber-${stop.order}`}
            label={
              stop.stop_type === STOP_TYPE.PICKUP ||
              stop.provider_stop_type?.value === STOP_TYPE.PICKUP
                ? 'Pick-up #'
                : stop.stop_type === STOP_TYPE.DROP_OFF ||
                  stop.provider_stop_type?.value === STOP_TYPE.DROP_OFF
                ? 'Delivery #'
                : 'Transfer #'
            }
            inputType={TwInputTypes.text}
            value={stopIdentifier}
            onInputChange={(e): void => {
              const newStop = { ...stop };
              newStop.provider_stop_identifier = e.target.value;
              newStop.stop_identifier = e.target.value;
              setStopIdentifier(e.target.value);
            }}
            onInputBlur={(e): void => {
              const newStop = { ...stop };
              newStop.provider_stop_identifier = stopIdentifier;
              newStop.stop_identifier = stopIdentifier;

              dispatch(updateStopV2({ stop: newStop }));
            }}
          />
        </Grid>
      </Grid>
      <StopNoteContactInfo
        isNoteVisible={!!stop.provider_include_location_notes}
        hasLocationBeenUpdated={hasLocationBeenUpdated}
        toggleNoteVisibility={(): void => {
          const newStop = {
            ...stop,
            provider_include_location_notes: !stop.provider_include_location_notes
          };
          //  dispatch(updateStopV2({ stop: newStop }));
        }}
        initialNote={stop.notes || ''}
        initialEmail={stop.contact?.contact_email || ''}
        initialName={stop.contact?.contact_name || ''}
        initialPhone={stop.contact?.contact_number || ''}
        initialExt={stop.contact?.contact_number_ext || ''}
        handleFieldChange={handleLocationNotesChange}
        handleLocationContactChange={handleLocationContactChange}
        isImportLoad={isImport}
        disableCheckbox={false}
      />

      <Box
        flexDirection={isImport || matchesMobileOrTablet ? 'column' : 'row'}
        className={isImport || matchesMobileOrTablet ? 'stop__import-date-time' : 'stop__date-time'}
      >
        <TwDatePicker
          name={`stop-date-${stop.order}`}
          label={`Scheduled date ${
            requiredFields.includes('start_date') || requiredFields.includes('end_date') ? '*' : ''
          }`}
          containerClassName="stop__date"
          value={stop.provider_date}
          placeholder="MM/DD/YY"
          onChange={(newDate): void => {
            const date = newDate as Date | null;
            setDate(date);
          }}
          onInputBlur={(e): void => {
            try {
              const date = new Date(e.target.value);
              setDate(date.toISOString() ? date : null);
            } catch (e) {
              setDate(null);
            }
          }}
          error={!!errors[`stop-date-${stop.order}`]}
        />
        <Box
          className={!isCreateOrder ? 'stop__time-container' : ''}
          display={isCreateOrder ? 'flex' : 'initial'}
        >
          <>
            <TwTimePicker
              name={`stop-time-from-${stop.order}`}
              label={`${enableAppointmentWindow ? 'From' : 'Scheduled time'} (${
                timezone_usage === TZ_USAGE.LOCAL && stop.provider_location
                  ? (stop.provider_location?.value as LoadLocation)?.short_offset
                  : userData?.short_offset
              })`}
              value={stop.provider_time_from}
              containerClassName={isImport ? 'stop__import-time-hide' : 'stop__time-hide'}
              onChange={(value): void => {
                const validInput = value !== 'hh:mm aa' && value !== 'hh:mm';
                const date = value as Date | null;
                validInput ? setTimeFrom(date) : setTimeFrom(null);
              }}
              onInputBlur={(e): void => {
                const validInput = e.target.value !== 'hh:mm aa' && e.target.value !== 'hh:mm';
                try {
                  const date = parse(
                    e.target.value as string,
                    !use24HourClock ? 'hh:mm aa' : 'HH:mm',
                    new Date()
                  );

                  validInput ? setTimeFrom(date) : setTimeFrom(null);
                } catch (e) {
                  setTimeFrom(null);
                }
              }}
              ampm={!use24HourClock}
              placeholder={!use24HourClock ? 'HH:MM AM' : 'HH:MM'}
            />

            {enableAppointmentWindow && (
              // Tabindex here helps focus on the second time field which was often missed in keyboard navigation
              <div tabIndex={0}>
                <TwTimePicker
                  name={`stop-time-to-${stop.order}`}
                  label={`To (${
                    timezone_usage === TZ_USAGE.LOCAL && stop.provider_location
                      ? (stop.provider_location?.value as LoadLocation)?.short_offset
                      : userData?.short_offset
                  })`}
                  value={stop.provider_time_to}
                  containerClassName={isImport ? 'stop__import-time-hide' : 'stop__time-hide'}
                  onChange={(value): void => {
                    const validInput = value !== 'hh:mm aa' && value !== 'hh:mm';
                    const date = value as Date | null;
                    validInput ? setTimeTo(date) : setTimeTo(null);
                  }}
                  onInputBlur={(e): void => {
                    const validInput = e.target.value !== 'hh:mm aa' && e.target.value !== 'hh:mm';
                    try {
                      const date = parse(
                        e.target.value as string,
                        !use24HourClock ? 'hh:mm aa' : 'HH:mm',
                        new Date()
                      );
                      validInput ? setTimeTo(date) : setTimeTo(null);
                    } catch (e) {
                      setTimeTo(null);
                    }
                  }}
                  ampm={!use24HourClock}
                  placeholder={!use24HourClock ? 'HH:MM AM' : 'HH:MM'}
                />
              </div>
            )}
          </>
        </Box>
      </Box>
    </Box>
  );
};

export default TwOnlyinfoStop;
